import * as Sentry from '@sentry/react';
import { credentials } from '@shootsta/client-auth';
import getOrgAlias from '@shootsta/get-alias';
import { getEnvironment } from '../utils/environment';

/**
 * Sentry configuration for error tracking and performance monitoring
 * Controlled by LaunchDarkly feature flag 'enable-sentry-logging'
 */

// Sentry DSN - this should be added to environment variables
const SENTRY_DSN = process.env.VITE_SENTRY_DSN || '';

/**
 * Gets comprehensive context for Sentry events
 * Centralizes all context gathering logic to avoid duplication
 */
export const getSentryContext = () => {
  const context: Record<string, Record<string, unknown>> = {};

  // User context
  try {
    const { user } = credentials.get() || {};
    if (user) {
      context.user = {
        id: user.userId,
        email: user.email,
        name:
          user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.email,
        firstName: user.firstName,
        lastName: user.lastName
      };
    }
  } catch (error) {
    console.warn('Failed to get user context for Sentry:', error);
  }

  // Organization context
  try {
    const orgAlias = getOrgAlias();
    if (orgAlias) {
      context.organization = {
        alias: orgAlias
      };
    }
  } catch (error) {
    console.warn('Failed to get organization context for Sentry:', error);
  }

  // Browser context
  if (typeof window !== 'undefined') {
    context.browser = {
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    };

    if (document.referrer) {
      context.browser.referrer = document.referrer;
    }
  }

  return context;
};

/**
 * Initialize Sentry with LaunchDarkly feature flag control
 * This allows us to enable/disable Sentry per environment via feature flags
 */
export const initSentryWithFeatureFlag = (): void => {
  if (!SENTRY_DSN) {
    return;
  }

  // Wait for LaunchDarkly to initialize, then check the feature flag
  if (typeof window !== 'undefined' && (window as any).launchDarklyClient) {
    (window as any).launchDarklyClient
      .waitForInitialization()
      .then(() => {
        const sentryEnabled = (window as any).launchDarklyClient.variation(
          'enable-sentry-logging',
          false
        );

        if (!sentryEnabled) {
          return;
        }

        try {
          Sentry.init({
            dsn: SENTRY_DSN,
            environment: getEnvironment(),
            integrations: [
              Sentry.browserTracingIntegration(),
              Sentry.replayIntegration()
            ],
            tracesSampleRate: 0.01, // 1% of transactions
            replaysSessionSampleRate: 0.0, // 0% of normal sessions (only errors)
            replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors will be recorded
            release: process.env.VITE_APP_VERSION || 'unknown',
            beforeSend(event) {
              // Add context to all events using centralized function
              const context = getSentryContext();

              if (context.user) {
                event.user = context.user as any;
              }

              if (context.organization) {
                event.tags = {
                  ...event.tags,
                  organization: context.organization.alias as string
                };
              }

              return event;
            }
          });
        } catch (error) {
          console.error('Failed to initialize Sentry:', error);
        }
      })
      .catch((error: unknown) => {
        console.error('Failed to initialize LaunchDarkly for Sentry:', error);
      });
  }
};

/**
 * Capture an error with additional context
 * Uses centralized context gathering
 */
export const captureError = (
  error: Error,
  additionalContext?: Record<string, Record<string, unknown>>
): void => {
  try {
    Sentry.withScope((scope) => {
      // Add centralized context
      const context = getSentryContext();
      Object.keys(context).forEach((key) => {
        scope.setContext(key, context[key]);
      });

      // Add any additional context
      if (additionalContext) {
        Object.keys(additionalContext).forEach((key) => {
          scope.setContext(key, additionalContext[key]);
        });
      }

      Sentry.captureException(error);
    });
  } catch (sentryError) {
    console.error('Failed to capture error with Sentry:', sentryError);
  }
};

/**
 * Capture a message/event with additional context
 * Uses centralized context gathering
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info',
  additionalContext?: Record<string, Record<string, unknown>>
): void => {
  try {
    Sentry.withScope((scope) => {
      // Add centralized context
      const context = getSentryContext();
      Object.keys(context).forEach((key) => {
        scope.setContext(key, context[key]);
      });

      // Add any additional context
      if (additionalContext) {
        Object.keys(additionalContext).forEach((key) => {
          scope.setContext(key, additionalContext[key]);
        });
      }

      scope.setLevel(level);
      Sentry.captureMessage(message);
    });
  } catch (sentryError) {
    console.error('Failed to capture message with Sentry:', sentryError);
  }
};

/**
 * Add breadcrumb for tracking user actions
 * Uses centralized context for additional data
 */
export const addBreadcrumb = (
  message: string,
  category = 'navigation',
  level: Sentry.SeverityLevel = 'info',
  data?: Record<string, unknown>
): void => {
  try {
    const context = getSentryContext();
    const breadcrumbData = {
      ...data,
      url: context.browser?.url,
      user: context.user?.email
    };

    Sentry.addBreadcrumb({
      message,
      category,
      level,
      data: breadcrumbData,
      timestamp: Date.now() / 1000
    });
  } catch (error) {
    console.error('Failed to add breadcrumb to Sentry:', error);
  }
};

export default Sentry;
